type GeneratedS3UrlObjectType = {
  objectKey: string;
  url?: string;
  fields?: Record<string, string>;
};

type S3FileUploadType = {
  generatedUrlObj: GeneratedS3UrlObjectType;
  file: File;
};

export type UploadResponseType = {
  response: Response;
  objectKey: string;
  s3Url: string;
  file: File;
};

/**
 * Handles uploading individual file. Should not be a public API and only called from uploadFilesToS3().
 * Note: The file will always attempt to upload once regardless of retries count.
 * @param fileToUpload - The file to upload
 * @param retries - The number of retries. This is in addition to the initial upload.
 *
 */
export const uploadFile = async (
  fileToUpload: S3FileUploadType,
  retries: number,
): Promise<UploadResponseType> => {
  let internalRetries = retries;
  let result: UploadResponseType;

  do {
    // Check if we're using pre-signed POST URL (preferred) or PUT URL (legacy)
    if (fileToUpload.generatedUrlObj.url && fileToUpload.generatedUrlObj.fields) {
      // Using pre-signed POST URL
      const formData = new FormData();

      // Add all the fields from the pre-signed POST response
      Object.entries(fileToUpload.generatedUrlObj.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Add the file as the last field (important for S3)
      formData.append("file", fileToUpload.file);

      result = await fetch(fileToUpload.generatedUrlObj.url, {
        method: "POST",
        body: formData,
      }).then((response) => {
        return {
          response,
          objectKey: fileToUpload.generatedUrlObj.objectKey,
          s3Url: fileToUpload.generatedUrlObj.url || "",
          file: fileToUpload.file,
        };
      });
    } else {
      throw new Error("Invalid S3 URL object: missing url/fields");
    }

    // Both POST and PUT methods should return 200 or 204 on success
    if (result.response.status === 200 || result.response.status === 204) {
      break;
    }
  } while (internalRetries-- > 0);
  return result;
};
